import { z } from 'zod';

import { SQUAD_DEFAULTS } from '../constants/squad';
import { LevelSchema, PoolStatusSchema, WeekdaySchema } from '../enums';
import { AvailabilitySchema, IdSchema, KopecksSchema } from './common';

/** Присоединиться к набору (BLUEPRINT 6: `pool.join(activityId, squadSize, availability?)`). */
export const PoolJoinInputSchema = z.object({
  activityId: IdSchema,
  squadSize: z
    .number()
    .int()
    .min(SQUAD_DEFAULTS.proposalSizeMin)
    .max(SQUAD_DEFAULTS.proposalSizeMax),
  /** Если не передано — берётся из профиля пользователя */
  availability: AvailabilitySchema.optional(),
  /** Уровень по активности, если ещё не указан в профиле */
  level: LevelSchema.optional(),
  /**
   * Ключ открытого набора, к которому присоединяются (раунд 3, N1). Сервер проверяет, что
   * профиль совместим с ячейкой: тот же город, активность и размер, вечер ячейки свободен,
   * уровень в полосе ячейки — иначе отказ с причиной. Без ключа — заявка по профилю.
   */
  cellKey: z.string().max(200).optional(),
});
export type PoolJoinInput = z.infer<typeof PoolJoinInputSchema>;

export const PoolWithdrawInputSchema = z.object({ applicationId: IdSchema });
export type PoolWithdrawInput = z.infer<typeof PoolWithdrawInputSchema>;

/** Краткая ссылка на активность в DTO пула и онбординга. */
export const ActivityBriefSchema = z.object({
  id: IdSchema,
  code: z.string(),
  name: z.string(),
});
export type ActivityBrief = z.infer<typeof ActivityBriefSchema>;

/** Статус своей заявки: прогресс ячейки и позиция (BLUEPRINT Stage 3, задача 2). */
export const PoolCellStatusSchema = z.object({
  applicationId: IdSchema,
  status: PoolStatusSchema,
  activity: ActivityBriefSchema,
  squadSize: z.number().int().positive(),
  /** Минимум для запуска отряда этого размера */
  minToLaunch: z.number().int().positive(),
  /** Людей в ячейке (WAITING с тем же ключом и пересечением дней), включая самого пользователя */
  count: z.number().int().nonnegative(),
  /** Позиция в очереди ячейки по priority, createdAt (с 1) */
  position: z.number().int().positive(),
  days: z.array(WeekdaySchema),
  from: z.string(),
  to: z.string(),
  levelBand: LevelSchema,
  /** Уровень у активности важен (раунд 4, N2): иначе полоса нейтральная — «любой уровень» */
  leveled: z.boolean(),
  createdAt: z.string(),
  /**
   * Ориентир цены сезона до сборки отряда (раунд 3, N3): `Activity.seasonPriceKopecks` или
   * `pricing.seasonDefault`, без персональных скидок. Точную цену человек видит на оплате.
   */
  seasonPriceKopecks: KopecksSchema,
});
export type PoolCellStatus = z.infer<typeof PoolCellStatusSchema>;

/** Предложение своей активности в статусе пользователя. */
export const MyProposalSchema = z.object({
  id: IdSchema,
  name: z.string(),
  sizeOptions: z.array(z.number().int()),
  status: z.enum(['PENDING', 'APPROVED', 'MERGED', 'REJECTED']),
  opsComment: z.string().nullable(),
  resultActivity: ActivityBriefSchema.nullable(),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
});
export type MyProposal = z.infer<typeof MyProposalSchema>;

/**
 * Набор, из которого человек вышел сам (раунд 4, N1): вернуться можно одним `pool.join` по
 * активности и размеру — вечера, районы и уровень берутся из профиля. Список строит сервер по
 * снятым заявкам, поэтому пункт «Вернуться в набор» переживает уход с экрана.
 */
export const PoolRejoinSchema = z.object({
  activity: ActivityBriefSchema,
  squadSize: z.number().int().positive(),
  withdrawnAt: z.string(),
});
export type PoolRejoin = z.infer<typeof PoolRejoinSchema>;

export const PoolStatusResultSchema = z.object({
  cells: z.array(PoolCellStatusSchema),
  /** Сам снятые заявки, по которым можно вернуться (свежие первыми, по одной на активность) */
  rejoin: z.array(PoolRejoinSchema),
  proposals: z.array(MyProposalSchema),
  /** Сколько ещё заявок можно подать */
  slotsLeft: z.number().int().nonnegative(),
  maxApplications: z.number().int().positive(),
  referralLink: z.string().nullable(),
  /** Фиче-флаги блоков экрана (Stage 11) */
  features: z.object({
    openSeats: z.boolean(),
    trial: z.boolean(),
  }),
});
export type PoolStatusResult = z.infer<typeof PoolStatusResultSchema>;

/** Открытый набор города: слот, а не люди (BLUEPRINT 3.5 «Открытые наборы»). */
export const OpenCellSchema = z.object({
  cellKey: z.string(),
  activity: ActivityBriefSchema,
  squadSize: z.number().int().positive(),
  minToLaunch: z.number().int().positive(),
  weekday: WeekdaySchema,
  levelBand: LevelSchema,
  /** Уровень у активности важен: иначе полоса нейтральная и на карточке пишется «любой уровень» */
  leveled: z.boolean(),
  /** Людей в ячейке (уникальные пользователи) */
  count: z.number().int().nonnegative(),
  /** Названия районов участников набора (до 3) */
  districtNames: z.array(z.string()),
  /** Пользователь уже в этом наборе */
  isMine: z.boolean(),
  /** Можно ли присоединиться (лимит заявок, нет заявки на эту активность) */
  canJoin: z.boolean(),
  /**
   * Почему нельзя, если `canJoin` = false и набор не свой. Без этого строка показывала набор
   * без кнопки и без единого слова: человек видел «Падел, вторник, 3 из 8» и не понимал, что
   * заявка по этой активности у него уже есть.
   */
  joinBlock: z
    .enum([
      'same_activity',
      /** Раунд 4, N2: своя заявка по этой активности на другой размер отряда */
      'same_activity_size',
      /** …другого уровня */
      'same_activity_level',
      /** …без этого вечера: добавь вечер в профиль — и заявка встанет и сюда */
      'same_activity_day',
      'in_squad',
      'limit',
      'day_not_free',
      'other_level',
    ])
    .nullable(),
  /**
   * Следующий шаг у набора, закрытого вечером или уровнем (раунд 4, N2): ключ набора той же
   * активности, куда можно вступить сразу; null — такого нет.
   */
  nextCellKey: z.string().nullable(),
  /**
   * У человека нет уровня по этой активности — при вступлении его спросят. Клиент показывает
   * выбор уровня сразу: раньше первое нажатие «В набор» уходило на сервер, возвращалось
   * `level_required`, и шторка молча дорастала — со стороны это выглядело как «кнопка не работает».
   */
  needsLevel: z.boolean(),
});
export type OpenCell = z.infer<typeof OpenCellSchema>;
